import { router } from '@inertiajs/react'
import { useState } from 'react'
import Layout from '@/components/layout/Layout'
import KanbanBoard from '@/components/boards/KanbanBoard'
import { useSync } from '@/hooks/useSync'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'

interface TaskLabel {
  id: string
  name: string
  color: string
}

interface TaskItem {
  id: string
  title: string
  description: string | null
  position: number
  priority: 'no_priority' | 'low' | 'medium' | 'high' | 'urgent'
  assignee: { id: string; name: string } | null
  labels: TaskLabel[]
  updated_at: string
}

interface Column {
  id: string
  name: string
  color: string | null
  position: number
  tasks: TaskItem[]
}

interface BoardDetail {
  id: string
  name: string
  slug: string
  description: string | null
  columns: Column[]
}

interface UserOption {
  id: string
  name: string
  email: string
}

interface Props {
  board: BoardDetail
  users: UserOption[]
}

export default function BoardsShow({ board, users }: Props) {
  useSync('boards')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [newName, setNewName] = useState(board.name)

  function handleRename(e: React.FormEvent) {
    e.preventDefault()
    if (!newName.trim()) return
    router.patch(`/boards/${board.slug}`, { board: { name: newName.trim() } }, {
      onSuccess: () => setRenameOpen(false),
    })
  }

  function handleDelete() {
    setDeleteOpen(false)
    router.delete(`/boards/${board.slug}`)
  }

  return (
    <Layout fullWidth>
      <div className="flex flex-col h-full p-6 overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">{board.name}</h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-600">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => { setNewName(board.name); setRenameOpen(true) }}>
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Rename board
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeleteOpen(true)}
                className="text-red-500 focus:text-red-500"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete board
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {board.description && (
          <p className="text-sm text-gray-500 mb-4">{board.description}</p>
        )}
        <div className="flex-1 min-h-0">
          <KanbanBoard board={board} users={users} />
        </div>
      </div>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename board</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="space-y-4">
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Board name"
              autoFocus
            />
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setRenameOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!newName.trim()}>Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this board?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-700">{board.name}</span> and all its tasks will be permanently deleted.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
